/**
 * siteSettings — small read/write helpers for site-level settings held in the
 * editor Y.Doc's "site" map (alongside nodes/edges/grids). Currently just the
 * nav-nesting toggle; persisted by the sqlite provider like the rest of the doc
 * and read at publish time by `publishSite`.
 */
import type * as Y from "yjs";
import {
  isSiteTheme,
  type SiteTheme,
  type SiteThemeOrLegacy,
} from "@website-builder/schemas";
import {
  fromLegacy,
  duro,
  CURRENT_GENERATION_VERSION,
} from "@website-builder/theme-engine";

const SITE_MAP = "site";

/** Whether the published nav should use the hierarchical (nested) renderer. */
export function readNavNested(ydoc: Y.Doc): boolean {
  const nav = ydoc.getMap(SITE_MAP).get("nav") as
    | { nested?: boolean }
    | undefined;
  return nav?.nested === true;
}

/** Persist the nav-nesting toggle (LOCAL write; mirrored by the provider). */
export function writeNavNested(ydoc: Y.Doc, nested: boolean): void {
  ydoc.getMap(SITE_MAP).set("nav", { nested });
}

/** Raw stored theme (new Brand shape OR legacy preset/knobs), if any. */
export function readSiteTheme(ydoc: Y.Doc): SiteThemeOrLegacy | undefined {
  return ydoc.getMap(SITE_MAP).get("theme") as SiteThemeOrLegacy | undefined;
}

/** Persist the Brand theme (always the new shape; LOCAL write, mirrored). */
export function writeSiteTheme(ydoc: Y.Doc, theme: SiteTheme): void {
  ydoc.getMap(SITE_MAP).set("theme", theme);
}

/**
 * The editable Brand theme for a site: the new shape passes through; a legacy
 * preset/knobs theme is read into Brand via `fromLegacy` (so the designer opens
 * pre-populated); an unset theme starts from the Duro default Brand. Never
 * mutates the stored doc — call `writeSiteTheme` to opt a legacy site in.
 */
export function effectiveSiteTheme(
  stored: SiteThemeOrLegacy | undefined,
): SiteTheme {
  if (stored && isSiteTheme(stored)) return stored;
  const brand = stored
    ? fromLegacy(stored.preset, stored.knobs)
    : duro.defaultBrand;
  return { brand, generationVersion: CURRENT_GENERATION_VERSION };
}

/**
 * Subscribe to every change in the site-settings map — including REMOTE ones.
 *
 * These settings used to be mirrored into React state and written twice (doc +
 * setState), seeded once when the doc loaded. That silently desynced: this doc
 * is also written by P2P peers, other tabs via the sync bridge, and undo, none
 * of which went through the setter. The drawer then showed something other
 * than what publish would read. With this, the doc is the only source of truth
 * and React just derives from it.
 */
export function subscribeSiteSettings(ydoc: Y.Doc, onChange: () => void) {
  const map = ydoc.getMap(SITE_MAP);
  map.observeDeep(onChange);
  return () => map.unobserveDeep(onChange);
}

/**
 * A `useSyncExternalStore`-safe snapshot of the effective Brand theme.
 *
 * `effectiveSiteTheme` builds a NEW object per call, which as a raw getSnapshot
 * would make React see a changed store on every render and loop forever. The
 * derived value is therefore cached against the stored value's identity: Yjs
 * hands back the same reference until the entry is replaced, so this recomputes
 * exactly when the theme actually changes.
 */
export function createSiteThemeSnapshot(ydoc: Y.Doc): () => SiteTheme {
  let lastRaw: SiteThemeOrLegacy | undefined;
  let lastDerived: SiteTheme | undefined;
  return () => {
    const raw = readSiteTheme(ydoc);
    if (lastDerived === undefined || raw !== lastRaw) {
      lastRaw = raw;
      lastDerived = effectiveSiteTheme(raw);
    }
    return lastDerived;
  };
}
